import { Button, Modal } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { DateTime } from 'luxon';

import { useAppSelector } from 'hooks';

interface IMigrationBlockedModalProps {
  status: IMigrationStatus;
  onClose: () => void;
}

/**
 * Archiving a patient mid-move is refused rather than confirmed. The reader is most
 * likely a clinician who does not know a migration exists, so this says what was refused, why it
 * would have done harm, and who can clear it — rather than only that it is not allowed.
 */
const MigrationBlockedModal = ({ status, onClose }: IMigrationBlockedModalProps) => {
  const { formatMessage } = useIntl();
  const locale = useAppSelector((state) => state.intl.locale);

  // No date is better than a wrong one: a missing measure_date would otherwise render 1970
  // inside the sentence explaining why a clinician cannot archive this patient.
  const exportedAt = status.exportedAt
    ? DateTime.fromMillis(status.exportedAt).setLocale(locale).toLocaleString(DateTime.DATE_SHORT)
    : null;

  return (
    <Modal show backdrop="static" data-test="migration-blocked-modal">
      <Modal.Header closeButton onHide={onClose}>
        <Modal.Title>{formatMessage({ id: 'migrationBlockedTitleKey' })}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          {exportedAt
            ? formatMessage(
                { id: 'migrationBlockedReasonKey' },
                { destination: status.destinationEnvironment, date: exportedAt }
              )
            : formatMessage(
                { id: 'migrationBlockedReasonNoDateKey' },
                { destination: status.destinationEnvironment }
              )}
        </p>
        <p className="mb-0">{formatMessage({ id: 'migrationBlockedConsequenceKey' })}</p>
        <p className="mb-0 mt-2">{formatMessage({ id: 'migrationBlockedNextStepKey' })}</p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={onClose} data-test="migration-blocked-close">
          {formatMessage({ id: 'closeKey' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MigrationBlockedModal;
